import { jsx } from 'react/jsx-runtime';
import SingletonRouter from 'next/router';
import { useRef, useState, useCallback, useEffect } from 'react';
import { logger } from '@ninja/utilities';
import { ConfirmDialog } from '@ninja/web';

const useLeavePage = (props) => {
    const { whiteListedRoutes, dialogProps, shouldPreventLeaving } = props;
    const temporaryAllowLeave = useRef(false);
    const { onClose, description, title, onConfirm, onCancel, variant = 'none', cancelLabel = 'Cancel', confirmLabel = 'Confirm', } = dialogProps;
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState({
        title,
        description,
    });
    const handleResolve = useRef(() => undefined);
    const stateRef = useRef(typeof window !== 'undefined' ? window.history.state : undefined);
    const isPopStateRef = useRef(false);
    const triggerLeaveMessage = useCallback(async (props) => {
        return await new Promise((resolve) => {
            setIsOpen(true);
            setMessages(props);
            handleResolve.current = resolve;
        });
    }, [setIsOpen]);
    useEffect(() => {
        var _a;
        const singletonRouter = SingletonRouter;
        if (typeof window == 'undefined')
            return;
        if (!((_a = singletonRouter === null || singletonRouter === void 0 ? void 0 : singletonRouter.router) === null || _a === void 0 ? void 0 : _a.change)) {
            return;
        }
        const originalChangeFunction = singletonRouter.router.change;
        const originalOnBeforeUnloadFunction = window.onbeforeunload;
        if (shouldPreventLeaving) {
            window.onbeforeunload = () => {
                if (temporaryAllowLeave.current)
                    return;
                else
                    return '';
            };
        }
        else {
            window.onbeforeunload = originalOnBeforeUnloadFunction;
        }
        if (shouldPreventLeaving) {
            singletonRouter.router.change = async (...args) => {
                var _a;
                const newRoute = args ? args[1] : '';
                if (whiteListedRoutes === null || whiteListedRoutes === void 0 ? void 0 : whiteListedRoutes.includes(newRoute !== null && newRoute !== void 0 ? newRoute : '')) {
                    originalChangeFunction.apply(singletonRouter.router, args);
                    return;
                }
                const confirmed = temporaryAllowLeave.current || (await triggerLeaveMessage({ title, description }));
                if (confirmed) {
                    try {
                        originalChangeFunction.apply(singletonRouter.router, args);
                    }
                    catch (error) {
                        logger.error(error);
                        return;
                    }
                }
                else if (isPopStateRef.current) {
                    const currentPath = (_a = singletonRouter.router) === null || _a === void 0 ? void 0 : _a.asPath;
                    const documentTitle = document.title;
                    window.history.pushState(stateRef.current, '', currentPath);
                    document.title = '';
                    setTimeout(() => {
                        document.title = documentTitle;
                    });
                    isPopStateRef.current = false;
                }
            };
        }
        else {
            if (singletonRouter === null || singletonRouter === void 0 ? void 0 : singletonRouter.router) {
                singletonRouter.router.change = originalChangeFunction;
            }
        }
        return () => {
            if (singletonRouter === null || singletonRouter === void 0 ? void 0 : singletonRouter.router) {
                singletonRouter.router.change = originalChangeFunction;
            }
            window.onbeforeunload = originalOnBeforeUnloadFunction;
        };
    }, [shouldPreventLeaving, triggerLeaveMessage, title, description, whiteListedRoutes]);
    useEffect(() => {
        if (SingletonRouter === null || SingletonRouter === void 0 ? void 0 : SingletonRouter.router) {
            SingletonRouter === null || SingletonRouter === void 0 ? void 0 : SingletonRouter.router.beforePopState(() => {
                isPopStateRef.current = true;
                return true;
            });
        }
        return () => {
            if (SingletonRouter === null || SingletonRouter === void 0 ? void 0 : SingletonRouter.router) {
                SingletonRouter === null || SingletonRouter === void 0 ? void 0 : SingletonRouter.router.beforePopState(() => true);
            }
        };
    }, []);
    const Component = (jsx(ConfirmDialog, { isOpen: isOpen, variant: variant, title: messages.title, description: messages.description, onClose: () => {
            setIsOpen(false);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }, buttonGroup: [
            {
                variant: 'secondary',
                label: confirmLabel,
                onClick: () => {
                    onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm();
                    setIsOpen(false);
                    handleResolve.current(true);
                },
            },
            {
                variant: 'primary',
                label: cancelLabel,
                onClick: () => {
                    onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                    setIsOpen(false);
                    handleResolve.current(false);
                },
            },
        ] }));
    const onAllowLeave = (callback) => {
        temporaryAllowLeave.current = true;
        return new Promise((resolve, reject) => {
            try {
                const callBackReturn = callback === null || callback === void 0 ? void 0 : callback();
                if (callBackReturn === null || callBackReturn === void 0 ? void 0 : callBackReturn.then) {
                    callBackReturn
                        .then(() => {
                        temporaryAllowLeave.current = false;
                        resolve();
                    })
                        .catch((error) => {
                        temporaryAllowLeave.current = false;
                        reject(error);
                    });
                }
                else {
                    temporaryAllowLeave.current = false;
                    resolve();
                }
            }
            catch (error) {
                temporaryAllowLeave.current = false;
                reject(error);
            }
        });
    };
    return { Component, triggerLeaveMessage, setIsOpen, isOpen, onAllowLeave };
};

export { useLeavePage as default };
